import React, { useCallback, useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { cn } from '@express-labs/raven-tools';
import { Brander } from '@express-labs/raven-brander';
import FocusCloseModal from './subcomponents/FocusCloseModal/FocusCloseModal';
import s from './Modal.scss';

export interface ModalProps extends ReactModal.Props {
  /** React content that is inserted after the close icon */
  afterCloseChildren?: React.ReactNode;
  /** React content that is inserted before the close icon */
  beforeCloseChildren?: React.ReactNode;
  /** A React component to display inside of modal when the modal is in it's opened state. */
  children: React.ReactNode;
  /** Additional css classes that are passed to the React-Modal component. */
  className?: string;
  /** Additional css classes to append to the \<section /> HTML element that wraps the close modal icon. */
  classNameCloseSection?: string;
  /** Additional css classes t append to the <FocusCloseModal /> component (aka, the close X icon) */
  classNameCloseIcon?: string;
  /** The number of milliseconds to wait before closing the modal. */
  closeTimeoutMS?: number;
  /** 'String indicating how the content container should be announced to screen readers. This sets the "aria-label" attribute.  A better option would be to set aria-labelledby and aria-description. See WCAG example above. */
  contentLabel: string;
  /** When true, the "close modal" button is automatically given focus once the modal opens. */
  focusOnOpen?: boolean;
  /** Automatically give focus to the close button When the contents of the modal dialog are updated. */
  focusOnUpdate?: boolean;
  /** Show or hide the default modal close button. */
  hideCloseButton?: boolean;
  /** Enables or restricts the user's ability to close the modal.  When true, the modal can only be closed programmatically */
  isCloseable?: boolean;
  /** Programmatically open or close the modal. */
  isOpen: boolean;
  /** Callback function called BEFORE the modal closes and is unmounted.  This function should be passed in from the viewModel container component. The function should set the isOpen prop to false to close. */
  onModalClose?: () => void;
  /** Additional css classes appended to the \<div /> tag holding the shadowbox overlay */
  overlayClassName?: string;
  /** Used by React-Modal, IF ariaHideApp is true, then this element is hidden when the modal is open.  By default it is set to "body > .content", to work with app_express.  WARNING!!! If it cannot find the element, it will revert to "body" and hide the modal itself from screen readers */
  setAppElement?: string | HTMLElement;
  /** Boolean to control the ability to close the modal by clicking on the "shadowbox" overlay. */
  shouldCloseOnOverlayClick?: boolean;
  /** @deprecated use closeIconClassName instead. At one point this did more.  But now, all this does is append a CSS class to the \<section /> HTML element that wraps the close modal icon. Same as the closeIconClassName prop. */
  view?: 'default' | 'full';
}

/**
 * Express's Modal component wraps and extends the open-source [React Modal](https://reactcommunity.org/react-modal/) module created by Bruno Dias.
 *
 * Read the documentation for ReactModal to learn about other possible component properties not listed in this Storybook.
 *
 * All props are passed through to ReactModal with the exception of the following:
 * - `onRequestClose` -- use onModalClose instead.
 * - `closeIconClassName`
 * - `focusOnOpen`
 * - `focusOnUpdate`
 * - `hideCloseButton`
 * - `isCloseable`
 * - `overlayClassName`
 * - `view`
 *
 * IMPORTANT: In order to be fully WCAG compliant you MUST return focus to the button or link that spawned this modal if the user clicks on the close button!
 *
 * ## Examples
 * ```html
 * <Modal contentLabel="Simple Example">
 *   <h1>Hello World!</h1>
 *   <p>Default modal</p>
 * </Modal>
 *
 * <!-- actual example from the app -->
 * <Modal
 * 	isOpen={this.state.isOpen}
 * 	role="dialog"
 * 	aria-labelledby="login-dialog-title"
 * 	aria-describedby="login-dialog-desc"
 * 	className={s.loginContainerModal}
 * 	onModalClose={this.onModalWindowClose}
 * >
 * 	 <div>
 * 		 <h1 id="login-dialog-title" className="srOnly">
 * 			 Sign in or checkout as guest modal dialog
 * 		 </h1>
 * 		 <p id="login-dialog-desc" className="srOnly">
 * 			 This dialog contains a form for signing into Express and a button for checking out
 * 			 as a guest.
 * 		 </p>
 * 		 <LoginContainer
 * 			 onLogin={this.onLogin}
 * 			 showGuest
 * 		 />
 *   </div>
 * </Modal>
 * ```
 *
 * ## Global Class Names
 * This is a map of the css classes for this component.  You can hook into these classes to further customize the Modal.
 * ```html
 * <div class="ReactModalPortal">
 * 	 <div class="ReactModal__Overlay ReactModal__Overlay--after-open ...">
 * 		 <div class="ReactModal__Content ReactModal__Content--after-open [props.className] ,,,">
 * 			 <section>
 * 				 ... [your content] ...
 * 			 </section>
 * 			 <section class="closeButton view closeIconClassName">
 * 				 <button>[ close button ]</button>
 * 			 </section>
 * 		 </div>
 * 	 </div>
 * </div>
 * ```
 **/
const Modal = ({
  // Props used locally
  afterCloseChildren = null,
  beforeCloseChildren = null,
  children,
  classNameCloseSection,
  classNameCloseIcon,
  focusOnOpen,
  focusOnUpdate,
  hideCloseButton = false,
  isCloseable = true,
  isOpen,
  overlayClassName,
  setAppElement,
  view,

  // Props passed through to React-Modal
  className,
  onModalClose,
  shouldCloseOnOverlayClick,
  ...passThroughProps
}: ModalProps) => {
  const [isOpenArg, setIsOpenArg] = useState<boolean>(isOpen);

  useEffect(() => {
    setIsOpenArg(isOpen);
  }, [isOpen]);

  const closeModal = useCallback(() => {
    if (isOpenArg) {
      if (typeof onModalClose === 'function') {
        onModalClose();
      }
    }
  }, [onModalClose, isOpenArg]);

  if (isOpenArg === true)
    ReactModal.setAppElement(setAppElement ?? document.body);

  return (
    <ReactModal
      shouldCloseOnOverlayClick
      isOpen={isOpenArg}
      overlayClassName={cn(overlayClassName, s.reactModalOverlay)}
      className={cn(className, s.reactModal)}
      onRequestClose={closeModal}
      {...passThroughProps}
    >
      <Brander>
        <section className={s.modalContent}>{children}</section>

        {isCloseable && !hideCloseButton && (
          <section
            className={cn(
              s.closeButton,
              view && s[view],
              classNameCloseSection
            )}
          >
            {beforeCloseChildren}
            <FocusCloseModal
              className={classNameCloseIcon}
              onButtonClick={closeModal}
              focusOnOpen={focusOnOpen}
              focusOnUpdate={focusOnUpdate}
              isOpen={isOpenArg}
            />
            {afterCloseChildren}
          </section>
        )}
      </Brander>
    </ReactModal>
  );
};

Modal.displayName = 'Modal';

export default Modal;
